// CASH1-S4 (docs/build-briefs/CASH1-S4-closeout-cash-and-tie-outs.md; PRD P1.1; DEC-399): the pure rules behind the closeout Cash
// block. No DOM, so a plain node:test file proves them (test/cash1-s4-ui-closeout-cash-model.test.ts) and closeoutCash.ts draws
// from them. There is no cash tip and no tip payout anywhere in the block (owner ruling 2026-10-13).

import type { CloseoutCashBlockDto } from "./api.ts";
import { formatCents } from "./money.ts";

export const CASH_NA = "-";

export interface CashTile {
  label: string;
  value: string;
  /** Over or short only: the sign of the figure. */
  tone?: "over" | "short" | "exact";
}

export interface CashLine {
  label: string;
  value: string;
  strong?: boolean;
}

export interface CashSessionRow {
  sessionId: string;
  title: string;
  /** Times are drawn by the screen in the device's own zone; the model carries the instants. */
  openedAt: string;
  closedAt: string;
  countedText: string;
  approved: boolean;
  /** CLOSEFIX1. The reason picked for the variance, when the caller may see it. */
  reason: string | null;
  /** CLOSEFIX1. Who approved the variance, when the name was recorded. */
  approverName: string | null;
  tag: string;
  tone: "over" | "short" | "exact";
}

export interface CashOpenRow {
  sessionId: string;
  title: string;
  openedAt: string;
}

export type CloseoutCashView =
  | { kind: "hidden" }
  | { kind: "na" }
  | {
      kind: "block";
      tiles: CashTile[];
      lines: CashLine[];
      sessions: CashSessionRow[];
      openDrawers: CashOpenRow[];
    };

/** "+$7.00", "-$7.00" or "$0.00". */
export function signedCents(cents: number): string {
  if (cents === 0) return formatCents(0);
  return `${cents > 0 ? "+" : "-"}${formatCents(Math.abs(cents))}`;
}

function hasActivity(block: CloseoutCashBlockDto): boolean {
  return (
    block.floatCents !== 0 ||
    block.cashSalesCents !== 0 ||
    block.cashRefundsCents !== 0 ||
    block.paidInCents !== 0 ||
    block.paidOutCents !== 0 ||
    block.dropsCents !== 0 ||
    block.sessions.length > 0 ||
    block.openDrawers.length > 0
  );
}

const toneOf = (cents: number): "over" | "short" | "exact" =>
  cents > 0 ? "over" : cents < 0 ? "short" : "exact";

function deviceTitle(deviceName: string | null, custodianName: string | null): string {
  const device = deviceName ?? CASH_NA;
  return custodianName === null ? device : `${device}, ${custodianName}`;
}

/** What the closeout screen shows for the Cash block. Hidden for a location that never took cash. N/A for a finalized day that
 * recorded no block (finalized before the feature) at a location whose cash is on. */
export function cashView(input: {
  cashEnabled: boolean;
  cash: CloseoutCashBlockDto | null;
  finalized: boolean;
}): CloseoutCashView {
  const { cash } = input;
  if (cash === null) {
    return input.finalized && input.cashEnabled ? { kind: "na" } : { kind: "hidden" };
  }
  if (!input.cashEnabled && !hasActivity(cash)) return { kind: "hidden" };
  const counted = cash.sessions.length > 0;
  // CASH1-S4-F1: the keys are absent for a caller without drawer.view_expected, so these read N/A.
  const expected = cash.expectedCents;
  const overShort = cash.overShortCents;
  const tiles: CashTile[] = [
    { label: "Cash sales", value: formatCents(cash.cashSalesCents) },
    { label: "Cash refunds", value: formatCents(cash.cashRefundsCents) },
    {
      label: "Expected",
      value: counted && expected !== undefined ? formatCents(expected) : CASH_NA,
    },
    { label: "Counted", value: counted ? formatCents(cash.countedCents) : CASH_NA },
    counted && overShort !== undefined
      ? { label: "Over / short", value: signedCents(overShort), tone: toneOf(overShort) }
      : { label: "Over / short", value: CASH_NA },
  ];
  const lines: CashLine[] = [
    { label: "Float", value: formatCents(cash.floatCents) },
    { label: "Cash sales", value: formatCents(cash.cashSalesCents) },
    {
      label: "Cash refunds",
      value:
        cash.cashRefundsCents === 0 ? formatCents(0) : `-${formatCents(cash.cashRefundsCents)}`,
    },
    { label: "Paid in", value: formatCents(cash.paidInCents) },
    {
      label: "Paid out",
      value: cash.paidOutCents === 0 ? formatCents(0) : `-${formatCents(cash.paidOutCents)}`,
    },
    {
      label: "Drops to safe",
      value: cash.dropsCents === 0 ? formatCents(0) : `-${formatCents(cash.dropsCents)}`,
    },
    {
      label: "Expected",
      value: counted && expected !== undefined ? formatCents(expected) : CASH_NA,
      strong: true,
    },
  ];
  return {
    kind: "block",
    tiles,
    lines,
    sessions: cash.sessions.map((s) => {
      const variance = s.varianceCents;
      const tone = variance === undefined ? "exact" : toneOf(variance);
      return {
        sessionId: s.sessionId,
        title: deviceTitle(s.deviceName, s.custodianName),
        openedAt: s.openedAt,
        closedAt: s.closedAt,
        countedText: formatCents(s.countedCents),
        approved: s.approverUserId !== undefined && s.approverUserId !== null,
        reason: s.reasonLabel ?? null,
        approverName: s.approverName ?? null,
        tag:
          variance === undefined
            ? CASH_NA
            : tone === "exact"
              ? "Exact"
              : `${tone === "over" ? "Over" : "Short"} ${formatCents(Math.abs(variance))}`,
        tone,
      };
    }),
    openDrawers: cash.openDrawers.map((o) => ({
      sessionId: o.sessionId,
      title: deviceTitle(o.deviceName, o.custodianName),
      openedAt: o.openedAt,
    })),
  };
}

/** Sales by day shows the Card and Cash columns only when some day in the range collected cash. */
export function showTenderColumns(days: readonly { cashCents: number }[]): boolean {
  return days.some((d) => d.cashCents > 0);
}
